import * as THREE from 'three'

export type UvProjection = 'box' | 'cylindrical' | 'planar' | 'none'

/**
 * Generates UV coordinates using box projection.
 * Projects UVs based on X and Y coordinates relative to bounding box.
 * Good for most general shapes.
 */
export function generateBoxUVs(geometry: THREE.BufferGeometry): void {
  geometry.computeBoundingBox()
  const bbox = geometry.boundingBox!
  const size = new THREE.Vector3()
  bbox.getSize(size)

  const position = geometry.getAttribute('position')
  const normal = geometry.getAttribute('normal')
  const uvs = new Float32Array(position.count * 2)

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i)
    const y = position.getY(i)
    const z = position.getZ(i)

    // Use normal to determine which face we're on for better projection
    const nx = normal ? Math.abs(normal.getX(i)) : 0
    const ny = normal ? Math.abs(normal.getY(i)) : 0
    const nz = normal ? Math.abs(normal.getZ(i)) : 0

    let u: number, v: number

    if (nx >= ny && nx >= nz) {
      // Side face (X-facing) - use Z and Y
      u = (z - bbox.min.z) / (size.z || 1)
      v = (y - bbox.min.y) / (size.y || 1)
    } else if (ny >= nx && ny >= nz) {
      // Top/bottom face (Y-facing) - use X and Z
      u = (x - bbox.min.x) / (size.x || 1)
      v = (z - bbox.min.z) / (size.z || 1)
    } else {
      // Front/back face (Z-facing) - use X and Y
      u = (x - bbox.min.x) / (size.x || 1)
      v = (y - bbox.min.y) / (size.y || 1)
    }

    uvs[i * 2] = u
    uvs[i * 2 + 1] = v
  }

  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
}

/**
 * Generates UV coordinates using cylindrical projection.
 * U = angle around Y axis (0 to 1), V = height (0 to 1).
 * Good for round objects like tumblers, cups, bottles.
 */
export function generateCylindricalUVs(geometry: THREE.BufferGeometry): void {
  geometry.computeBoundingBox()
  const bbox = geometry.boundingBox!
  const size = new THREE.Vector3()
  bbox.getSize(size)

  const position = geometry.getAttribute('position')
  const uvs = new Float32Array(position.count * 2)

  // Calculate center for cylindrical projection
  const center = new THREE.Vector3()
  bbox.getCenter(center)

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i) - center.x
    const y = position.getY(i)
    const z = position.getZ(i) - center.z

    // U = angle around Y axis (0 to 1)
    const u = (Math.atan2(z, x) + Math.PI) / (2 * Math.PI)
    // V = height (0 to 1)
    const v = (y - bbox.min.y) / (size.y || 1)

    uvs[i * 2] = u
    uvs[i * 2 + 1] = v
  }

  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
}

/**
 * Generates UV coordinates using planar projection from the front (Z direction).
 * Projects UVs based on X and Y coordinates only.
 * Good for flat or front-facing surfaces.
 */
export function generatePlanarUVs(geometry: THREE.BufferGeometry): void {
  geometry.computeBoundingBox()
  const bbox = geometry.boundingBox!
  const size = new THREE.Vector3()
  bbox.getSize(size)

  const position = geometry.getAttribute('position')
  const uvs = new Float32Array(position.count * 2)

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i)
    const y = position.getY(i)

    // Simple planar projection using X and Y
    const u = (x - bbox.min.x) / (size.x || 1)
    const v = (y - bbox.min.y) / (size.y || 1)

    uvs[i * 2] = u
    uvs[i * 2 + 1] = v
  }

  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
}

/**
 * Applies UV generation to a geometry based on the specified projection type.
 */
export function applyUvProjection(geometry: THREE.BufferGeometry, projection: UvProjection): void {
  switch (projection) {
    case 'box':
      generateBoxUVs(geometry)
      break
    case 'cylindrical':
      generateCylindricalUVs(geometry)
      break
    case 'planar':
      generatePlanarUVs(geometry)
      break
    case 'none':
      // Don't generate UVs - preserve originals from GLB
      break
  }
}
